import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetUserActiveCreditFacilitiesQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import {
  PortfolioTabOptionKeys,
  PortfolioTemplate,
} from 'packages/portfolio/ui/shared/PortfolioTemplate';
import { PortfolioSummaryContainer } from 'packages/portfolio/ui/summary/PortfolioSummaryContainer';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

export const PortfolioSummaryView = () => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQueryParams();
  const creditFacilityId = query.get(creditFacilityIdQueryParam);

  let routeLevelError = false;

  const { data: userData } = useGetUserActiveCreditFacilitiesQuery({
    variables: {
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });
  if (!userData || !userData.user) {
    return <Skeleton data-testid="loading-portfolio-summary" height="600px" />;
  }

  const {
    user: { organizationId, organization: creditFacilities },
  } = userData;

  const creditFacilitySlug = _.find(
    creditFacilities.creditFacilities,
    ({ id }) => id === creditFacilityId,
  )?.slug;

  if (!creditFacilityId || !creditFacilitySlug) {
    navigate('/portfolio/overview/facilities');
    toast({
      ...errorToastConfig,
      id: 'errorLoadPortfolioSummaryNoCreditFacility',
      title: 'Please select a credit facility.',
    });
    return <></>;
  }

  return (
    <Box width="100%" padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            routeLevelError = true;
            toast({
              ...errorToastConfig,
              id: 'errorLoadPortfolioSummary',
              title: 'Error loading portfolio summary route',
            });
            navigate('/');
          }
        }}
      >
        <PortfolioTemplate
          organizationId={organizationId}
          creditFacilitySlug={creditFacilitySlug}
          selectedTab={PortfolioTabOptionKeys.summary}
        >
          <PortfolioSummaryContainer
            creditFacilityId={creditFacilityId}
            capitalProviderOrganizationIds={[organizationId]}
            dashboard="capitalProvider"
          />
        </PortfolioTemplate>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
